import Immutable from 'immutable'
import { getFormSyncErrors } from 'redux-form'

import { actionTypes } from 'actions/actionTypes'
import {
  basketPromoCodeChange,
  basketPromoCodeAppliedChange,
  basketReset,
  basketPreviewOrderChange,
} from 'actions/basket'
import {
  feLoggingLogEvent,
  logLevels,
  trackSuccessfulCheckoutFlow,
  trackFailedCheckoutFlow,
} from 'actions/log'
import { trackSignupFinished } from 'actions/loggingmanager'
import loginActions from 'actions/login'
import statusActions from 'actions/status'
import tempActions from 'actions/temp'
import {
  trackAffiliatePurchase,
  trackUTMAndPromoCode,
  trackCheckoutError,
  trackSubscriptionCreated,
  clearTapjoyData,
  clearRoktData,
  trackUnexpectedSignup,
  trackFirstPurchase,
  trackNewUser,
  trackNewOrder,
} from 'actions/tracking'
import * as trackingKeys from 'actions/trackingKeys'
import { applePayStatusFetchError, placeOrder } from 'actions/trackingKeys'
import { fetchAddressByPostcode } from 'apis/addressLookup'
import { validateUserPassword } from 'apis/auth'
import { fetchReference, customerSignup } from 'apis/customers'
import { gaTrackingConfig } from 'config/head/gaTracking'
import { checkoutConfig } from 'routes/Checkout/checkoutConstants'
import {
  isCardPayment,
  getPaymentAuthData,
  getPaymentData,
  getPaymentProvider,
  isApplePayPayment,
} from 'routes/Checkout/checkoutPaymentSelectors'
import {
  authPayment,
  checkPayment,
  fetchPayPalToken,
  signupPayment,
} from 'routes/Checkout/checkoutPaymentsApis'
import {
  accountFormName,
  deliveryFormName,
  getPasswordValue,
  getSignupE2ETestName,
  getFormattedPhoneNumber,
  getApplePayDeliveryAddress,
  getApplePayPersonalInfo,
  getApplePayDeliveryInstruction,
  getApplePayDeliveryInstructionCustom,
} from 'routes/Checkout/checkoutSelectors'
import { getAddress } from 'routes/Checkout/checkoutUtils'
import { isApplePayAvailable } from 'routes/Checkout/utils/applePaySessionUtils'
import { checkoutCreatePreviewOrder } from 'routes/Menu/actions/checkout'
import { PaymentMethod, signupConfig } from 'routes/Signup/signupConfig'
import { getPreviewOrderId, getPromoCode, getBasketRecipes } from 'selectors/basket'
import { getSessionId } from 'selectors/cookies'
import { getNDDFeatureValue, getIsGoustoOnDemandEnabled } from 'selectors/features'
import { getUserLoginError } from 'selectors/status'
import { getUTMAndPromoCode } from 'selectors/tracking'
import Cookies from 'utils/GoustoCookies'
import GoustoException from 'utils/GoustoException'
import { basketResetPersistent } from 'utils/basket'
import { getDeliveryTariffId } from 'utils/deliveries'
import { getEnvironment } from 'utils/isomorphicEnvironment'
import logger from 'utils/logger'
import { isValidPromoCode } from 'utils/order'

export { checkoutTransactionalOrder } from '../Menu/actions/checkout'

export const checkoutUrgencySetCurrentStatus = (currentStatus) => ({
  type: actionTypes.CHECKOUT_URGENCY_SET_CURRENT_STATUS,
  currentStatus,
})

export const trackCheckoutUrgencyAction =
  (type, additionalData = {}) =>
  (dispatch, getState) => {
    const { promoCode, UTM } = getUTMAndPromoCode(getState())

    dispatch({
      type,
      trackingData: {
        actionType: type,
        ...UTM,
        promoCode,
        ...additionalData,
      },
    })
  }

function buildSignupRequestData(state) {
  const { form, basket, promoAgeVerified, checkout } = state

  const isApplePay = isApplePayPayment(state)
  const applePayPersonalInfo = getApplePayPersonalInfo(state)

  const account = Immutable.fromJS(form[accountFormName].values).get('account')
  const payment = Immutable.fromJS(form.payment.values).get('payment')
  const delivery = Immutable.fromJS(form[deliveryFormName]?.values)?.get('delivery')

  const orderId = getPreviewOrderId(state)
  const promoCode = getPromoCode(state) || ''
  const sessionId = getSessionId()

  const tariffId = basket.get('tariffId', '')
  const phoneNumber = getFormattedPhoneNumber(
    isApplePay ? applePayPersonalInfo.phoneNumber : delivery.get('phone', ''),
  )
  const email = account.get('email')
  const firstName = isApplePay ? applePayPersonalInfo.firstName : delivery.get('firstName').trim()
  const lastName = isApplePay ? applePayPersonalInfo.lastName : delivery.get('lastName').trim()
  const password = getPasswordValue(state)
  const ageVerified = Number(promoAgeVerified || false)
  const allowEmail = Number(account.get('allowEmail') || false)
  const allowThirdParty = Number(account.get('allowThirdPartyEmail') || false)
  const deliveryTariffId = getDeliveryTariffId(null, getNDDFeatureValue(state))
  const goustoRef = checkout.get('goustoRef')

  const deliveryInstructions = isApplePay
    ? getApplePayDeliveryInstructionCustom(state) || getApplePayDeliveryInstruction(state)
    : delivery.get('deliveryInstructionsCustom') || delivery.get('deliveryInstruction')
  const deliveryAddress = isApplePay ? getApplePayDeliveryAddress(state) : getAddress(delivery)
  const billingAddress =
    isCardPayment(state) && payment.get('isBillingAddressDifferent')
      ? getAddress(payment)
      : deliveryAddress

  const intervalId = delivery?.get('interval_id', 1) ?? 1
  const deliverySlotId = basket.get('slotId')
  const boxId = basket.get('boxId')

  const isGoustoOnDemandEnabled = getIsGoustoOnDemandEnabled(state)
  const subscription = isGoustoOnDemandEnabled
    ? { paused: Number(isGoustoOnDemandEnabled || false) }
    : {}

  return {
    order_id: orderId,
    promocode: promoCode,
    session_id: sessionId,
    customer: {
      tariff_id: tariffId,
      phone_number: phoneNumber,
      email,
      name_first: firstName,
      name_last: lastName,
      promo_code: promoCode,
      password,
      age_verified: ageVerified,
      marketing_do_allow_email: allowEmail,
      marketing_do_allow_thirdparty: allowThirdParty,
      delivery_tariff_id: deliveryTariffId,
      gousto_ref: goustoRef,
    },
    addresses: {
      shipping_address: {
        type: signupConfig.address_types.shipping,
        delivery_instructions: deliveryInstructions,
        ...deliveryAddress,
      },
      billing_address: {
        type: signupConfig.address_types.billing,
        ...billingAddress,
      },
    },
    subscription: {
      interval_id: intervalId,
      delivery_slot_id: deliverySlotId,
      box_id: boxId,
      ...subscription,
    },
    decoupled: {
      payment: 1,
    },
  }
}

export function userSubscribe({ pricing }) {
  return async (dispatch, getState) => {
    const state = getState()
    const boxId = state.basket.get('boxId')
    if (!boxId) {
      dispatch(
        feLoggingLogEvent(
          logLevels.error,
          'userSubscribe called with missing boxId: unexpected signup',
        ),
      )
      dispatch(trackUnexpectedSignup())

      return
    }
    dispatch(feLoggingLogEvent(logLevels.info, 'userSubscribe started', { boxId }))
    dispatch(statusActions.error(actionTypes.USER_SUBSCRIBE, null))
    dispatch(statusActions.pending(actionTypes.USER_SUBSCRIBE, true))

    const signupTestName = getSignupE2ETestName(state)
    try {
      const reqData = buildSignupRequestData(state)

      const { data } = await customerSignup(null, reqData)

      if (data.customer && data.addresses && data.subscription && data.orderId) {
        const {
          customer,
          addresses,
          subscription,
          orderId,
          customer: { id: customerId },
        } = data
        let user = Immutable.fromJS({
          ...customer,
          ...addresses,
          subscription,
        })
        user = user.set('goustoReference', user.get('goustoReference').toString())

        dispatch(trackNewUser(customerId))

        dispatch({
          type: actionTypes.CHECKOUT_ORDER_PLACED,
          trackingData: {
            actionType: placeOrder,
            order_id: orderId,
            order_total: pricing?.total,
            promo_code: getPromoCode(state),
            signup: true,
            subscription_active: data.subscription.status ? data.subscription.status.slug : true,
            interval_id: reqData.subscription.interval_id,
          },
        })

        dispatch(trackFirstPurchase(orderId, pricing))
        dispatch(trackNewOrder(orderId, customerId))
        dispatch(basketPreviewOrderChange(orderId, state.basket.get('boxId')))
        dispatch({ type: actionTypes.USER_SUBSCRIBE, user })
        dispatch(
          trackSuccessfulCheckoutFlow('userSubscribe: customerSignup successful', {
            testName: signupTestName,
            data,
          }),
        )
      } else {
        throw new GoustoException(actionTypes.USER_SUBSCRIBE)
      }
    } catch (err) {
      dispatch(statusActions.error(actionTypes.USER_SUBSCRIBE, err.message))
      dispatch(trackNewUser())

      const previewOrderId = getPreviewOrderId(state)

      dispatch({
        type: actionTypes.CHECKOUT_ORDER_FAILED,
        trackingData: {
          actionType: 'Order Failed',
          order_id: previewOrderId,
          promo_code: getPromoCode(state),
          signup: true,
          error_reason: err.message,
        },
      })
      dispatch(trackNewOrder(previewOrderId))
      logger.error({ message: err.message, errors: [err] })
      dispatch(
        trackFailedCheckoutFlow('userSubscribe: customerSignup failed', err, {
          testName: signupTestName,
        }),
      )
      throw err
    } finally {
      dispatch(statusActions.pending(actionTypes.USER_SUBSCRIBE, false))
    }
  }
}

/**
 * Fetches Apple Pay status - if browser supports Apple Pay and application can make payments via Apple Pay services.
 */
export const fetchApplePayStatus = (dispatch, _getState) => {
  isApplePayAvailable()
    .then((isAvailable) => {
      dispatch({
        type: actionTypes.CHECKOUT_LOAD_APPLE_PAY_ENABLED_STATUS,
        payload: isAvailable,
      })
    })
    .catch((error) => {
      dispatch({
        type: actionTypes.APPLE_PAY_STATUS_FETCH_ERROR,
        trackingData: {
          actionType: applePayStatusFetchError,
          error,
        },
      })
      dispatch({
        type: actionTypes.CHECKOUT_LOAD_APPLE_PAY_ENABLED_STATUS,
        payload: false,
      })
    })
}

const { pending, error } = statusActions
const { errorsThatClearOrderPreview, errorsToHandleForSignupPayment, passwordRules } =
  checkoutConfig

/* eslint-disable no-use-before-define */
export const checkoutActions = {
  checkoutClearErrors,
  checkoutCreatePreviewOrder,
  checkoutPostSignup,
  checkoutAddressLookup,
  checkoutSignup,
  checkoutCardAuthorisation,
  checkoutSignupPayment,
  fetchGoustoRef,
  clearGoustoRef,
  resetDuplicateCheck,
  trackSignupPageChange,
  trackingOrderPlaceAttempt,
  trackingOrderPlaceAttemptFailed,
  trackingOrderPlaceAttemptSucceeded,
  trackPromocodeChange,
  userSubscribe,
}
/* eslint-enable no-use-before-define */

const errorCodes = {
  duplicateDetails: '409-duplicate-details',
  promoCodeHasBeenUsed: '409-offer-has-been-used',
  challengeFailed: '3ds-challenge-failed',
  signupLoginFailed: 'signupLoginFailed',
}

function resetDuplicateCheck({ pricing }) {
  return (dispatch, getState) => {
    const state = getState()
    dispatch(error(actionTypes.CHECKOUT_ERROR_DUPLICATE, null))
    if (getPromoCode(state)) {
      if (!isValidPromoCode(pricing)) {
        dispatch(basketPromoCodeChange(''))
        dispatch(basketPromoCodeAppliedChange(false))
        dispatch(error(actionTypes.CHECKOUT_ERROR_DUPLICATE, true))
      }
    }
  }
}

export function checkoutClearErrors() {
  return {
    type: actionTypes.CHECKOUT_ERRORS_CLEAR,
  }
}

export function checkoutAddressLookup(postcode) {
  return async (dispatch) => {
    dispatch(pending(actionTypes.CHECKOUT_ADDRESSES_RECEIVE, true))
    dispatch(error(actionTypes.CHECKOUT_ADDRESSES_RECEIVE, null))

    const addresses = {
      deliveryPoints: [],
      postcode,
    }

    try {
      const lookupResults = await fetchAddressByPostcode(postcode)

      addresses.deliveryPoints = lookupResults.data.deliveryPoints || []
      addresses.town = lookupResults.data.town
      addresses.postcode = lookupResults.data.postcode || postcode
      addresses.county = lookupResults.data.traditionalCounty || ''
    } catch (err) {
      dispatch(
        trackCheckoutError(
          actionTypes.CHECKOUT_ADDRESSES_RECEIVE,
          err.message,
          'checkoutAddressLookup',
        ),
      )
      dispatch(error(actionTypes.CHECKOUT_ADDRESSES_RECEIVE, err.message))
      logger.error({ message: 'Unable to look-up address' })
    } finally {
      dispatch(pending(actionTypes.CHECKOUT_ADDRESSES_RECEIVE, false))
    }

    return addresses
  }
}

export const fireCheckoutPendingEvent =
  (pendingName, checkoutValue = true) =>
  (dispatch) => {
    dispatch(pending(pendingName, checkoutValue))
  }

export const fireCheckoutError =
  (errorName, errorValue = true) =>
  (dispatch) => {
    dispatch(trackCheckoutError(errorName, errorValue, 'fireCheckoutError'))
    dispatch(error(errorName, errorValue))
  }

// Note: this is a helper method, not an action.  It should be called directly
// instead of dispatched.
export const handlePromoCodeRemoved = async (dispatch) => {
  dispatch(basketPromoCodeChange(''))
  dispatch(basketPromoCodeAppliedChange(false))
  dispatch(error(actionTypes.CHECKOUT_ERROR_DUPLICATE, true))
}

export const handleCheckoutError = async (err, initiator, dispatch, getState) => {
  const { code } = err
  const errorName = errorsToHandleForSignupPayment.includes(code)
    ? actionTypes.CHECKOUT_PAYMENT
    : actionTypes.CHECKOUT_SIGNUP

  logger.error({ message: `${errorName} - ${err.message}`, errors: [err] })

  dispatch(trackCheckoutError(errorName, code, initiator))
  dispatch(error(errorName, code))
  if (code === errorCodes.duplicateDetails || code === errorCodes.promoCodeHasBeenUsed) {
    await handlePromoCodeRemoved(dispatch, getState)
  } else if (errorsThatClearOrderPreview.includes(code)) {
    // Certain error scenarios trigger the rollback logic which removes the
    // order preview, hence the preview order id stored at the client becomes
    // invalid. Regenerate the order preview when such an error is detected.
    await dispatch(checkoutCreatePreviewOrder())
  }
}

export function checkoutSignup({ pricing }) {
  return async (dispatch, getState) => {
    dispatch(feLoggingLogEvent(logLevels.info, 'checkoutSignup started'))
    dispatch(checkoutActions.trackSignupPageChange('Submit'))

    dispatch(error(actionTypes.CHECKOUT_SIGNUP, null))
    dispatch(pending(actionTypes.CHECKOUT_SIGNUP, true))

    try {
      await dispatch(checkoutActions.fetchGoustoRef())
      dispatch(checkoutActions.resetDuplicateCheck({ pricing }))
      await dispatch(checkoutActions.userSubscribe({ pricing }))
    } catch (err) {
      dispatch(feLoggingLogEvent(logLevels.error, `checkoutSignup: Signup failed: ${err.message}`))
      await handleCheckoutError(err, 'checkoutSignup', dispatch, getState)
      if (err.code !== errorCodes.duplicateDetails) {
        dispatch(checkoutActions.clearGoustoRef())
      }
      dispatch(pending(actionTypes.CHECKOUT_SIGNUP, false))

      return
    }

    if (isCardPayment(getState())) {
      dispatch(
        feLoggingLogEvent(
          logLevels.info,
          'checkoutSignup: userSubscribe finished, proceed to checkoutCardAuthorisation',
        ),
      )
      await dispatch(checkoutActions.checkoutCardAuthorisation({ pricing }))
    } else {
      dispatch(
        feLoggingLogEvent(
          logLevels.info,
          'checkoutSignup: userSubscribe finished, proceed to checkoutSignupPayment',
        ),
      )
      const sourceId = null
      await dispatch(checkoutActions.checkoutSignupPayment(sourceId, { pricing }))
    }
  }
}

export function checkoutCardAuthorisation({ pricing }) {
  return async (dispatch, getState) => {
    try {
      const reqData = getPaymentAuthData(getState(), { pricing })
      if (reqData.order_id) {
        const { data } = await authPayment(reqData, getSessionId())
        dispatch({
          type: actionTypes.PAYMENT_SHOW_MODAL,
          challengeUrl: data.responsePayload.redirectLink,
        })
        dispatch(trackUTMAndPromoCode(trackingKeys.signupChallengeModalDisplay))
      } else {
        dispatch(feLoggingLogEvent(logLevels.error, 'Auth payment failed: order_id does not exist'))
      }
    } catch (err) {
      dispatch(feLoggingLogEvent(logLevels.error, `Auth payment failed: ${err.message}`))
      await handleCheckoutError(err, 'checkoutCardAuthorisation', dispatch, getState)
      dispatch(checkoutActions.clearGoustoRef())
      dispatch(pending(actionTypes.CHECKOUT_SIGNUP, false))
    }
  }
}

export function checkoutSignupPayment(sourceId, { pricing }) {
  return async (dispatch, getState) => {
    dispatch(feLoggingLogEvent(logLevels.info, 'checkoutSignupPayment started'))
    try {
      const state = getState()
      const sessionId = getSessionId()
      const provider = getPaymentProvider(state)
      const reqData = getPaymentData(state)

      if (sourceId) {
        reqData.card_token = sourceId
      }

      await signupPayment(reqData, provider, sessionId)
      dispatch(
        feLoggingLogEvent(
          logLevels.info,
          'checkoutSignupPayment: signupPayment succeeded, proceed to checkoutPostSignup',
        ),
      )
      await dispatch(checkoutActions.checkoutPostSignup({ pricing }))
    } catch (err) {
      dispatch(
        feLoggingLogEvent(
          logLevels.error,
          `checkoutSignupPayment: Signup payment failed: ${err.message}`,
        ),
      )
      await handleCheckoutError(err, 'checkoutSignupPayment', dispatch, getState)
    } finally {
      dispatch(checkoutActions.clearGoustoRef())
      dispatch(pending(actionTypes.CHECKOUT_SIGNUP, false))
      dispatch(feLoggingLogEvent(logLevels.info, 'checkoutSignupPayment: in finally'))
    }
  }
}

export const checkPaymentAuth =
  (checkoutSessionId, { pricing }) =>
  async (dispatch, getState) => {
    dispatch({ type: actionTypes.PAYMENT_HIDE_MODAL })
    dispatch(pending(actionTypes.CHECKOUT_SIGNUP, true))

    try {
      const { data } = await checkPayment(checkoutSessionId, getSessionId())
      if (data && data.approved) {
        dispatch(trackUTMAndPromoCode(trackingKeys.signupChallengeSuccessful))

        dispatch(feLoggingLogEvent(logLevels.info, 'signup 3ds challenge success'))
        await dispatch(checkoutActions.checkoutSignupPayment(data.sourceId, { pricing }))
      } else {
        dispatch(feLoggingLogEvent(logLevels.info, 'signup 3ds challenge failed'))
        dispatch(trackUTMAndPromoCode(trackingKeys.signupChallengeFailed))
        dispatch(
          trackCheckoutError(
            actionTypes.CHECKOUT_SIGNUP,
            errorCodes.challengeFailed,
            'checkPaymentAuth',
          ),
        )
        dispatch(error(actionTypes.CHECKOUT_SIGNUP, errorCodes.challengeFailed))
        await dispatch(checkoutCreatePreviewOrder())
        dispatch(checkoutActions.clearGoustoRef())
        dispatch(pending(actionTypes.CHECKOUT_SIGNUP, false))
      }
    } catch (err) {
      dispatch(feLoggingLogEvent(logLevels.error, `Check payment auth failed: ${err.message}`))
      await handleCheckoutError(err, 'checkPaymentAuth', dispatch, getState)
      dispatch(checkoutActions.clearGoustoRef())
      dispatch(pending(actionTypes.CHECKOUT_SIGNUP, false))
    }
  }

export function checkoutPostSignup({ pricing }) {
  return async (dispatch, getState) => {
    dispatch(feLoggingLogEvent(logLevels.info, 'checkoutPostSignup started'))
    dispatch(trackSubscriptionCreated({ pricing }))
    const state = getState()

    const orderId = getPreviewOrderId(state)
    const basketRecipes = getBasketRecipes(state)
    dispatch({ type: actionTypes.CHECKOUT_SIGNUP_SUCCESS, orderId, basketRecipes, pricing }) // used for data layer tracking
    dispatch(tempActions.temp('originalGrossTotal', pricing.grossTotal))
    dispatch(tempActions.temp('originalNetTotal', pricing.netTotal))
    dispatch(trackPurchase({ orderId, pricing }))

    // this triggers the reCAPTCHA execution in PayWithApplePay component or CheckoutPayment component
    // on success user will log in automatically
    if (isApplePayPayment(getState())) {
      dispatch({
        type: actionTypes.APPLE_PAY_START_RECAPTCHA_EXECUTION,
        executionStarted: true,
      })
    } else {
      dispatch({
        type: actionTypes.CHECKOUT_SIGNUP_START_RECAPTCHA_EXECUTION,
        executionStarted: true,
      })
    }
  }
}

export const loginAfterSignup = (recaptchaToken) => async (dispatch, getState) => {
  dispatch({ type: actionTypes.CHECKOUT_SIGNUP_START_RECAPTCHA_EXECUTION, executionStarted: false })
  dispatch({ type: actionTypes.APPLE_PAY_START_RECAPTCHA_EXECUTION, executionStarted: false })
  dispatch(error(actionTypes.CHECKOUT_SIGNUP_LOGIN, null))
  dispatch(pending(actionTypes.CHECKOUT_SIGNUP_LOGIN, true))

  const state = getState()

  const accountValues = Immutable.fromJS(state.form[accountFormName].values)
  const account = accountValues.get('account')
  const email = account.get('email')
  const password = getPasswordValue(state)
  const orderId = getPreviewOrderId(state)

  await dispatch(
    loginActions.loginUser({ email, password, rememberMe: true, recaptchaToken }, orderId),
  )

  const stateAfterLogin = getState()
  const loginError = getUserLoginError(stateAfterLogin)
  if (loginError) {
    dispatch(
      feLoggingLogEvent(logLevels.info, `checkoutPostSignup: signup login failed: ${loginError}`),
    )
    dispatch(
      trackCheckoutError(
        actionTypes.CHECKOUT_SIGNUP_LOGIN,
        errorCodes.signupLoginFailed,
        'checkoutPostSignup',
      ),
    )
    dispatch(error(actionTypes.CHECKOUT_SIGNUP_LOGIN, true))
  } else {
    dispatch(feLoggingLogEvent(logLevels.info, 'checkoutPostSignup: signup login success'))
    dispatch(trackSignupFinished({ email }))
    basketResetPersistent(Cookies)
    dispatch(basketReset())
  }

  dispatch(pending(actionTypes.CHECKOUT_SIGNUP_LOGIN, false))
}

export const trackPurchase =
  ({ orderId, pricing }) =>
  (dispatch, getState) => {
    const state = getState()
    const promoCode = getPromoCode(state)
    const { grossTotal: totalPrice, deliveryTotal: shippingPrice, total } = pricing

    const gaIDTracking = gaTrackingConfig[getEnvironment()]

    if (typeof ga !== 'undefined') {
      ga('create', gaIDTracking, 'auto', 'gousto')
      ga('gousto.require', 'ec')
      ga('gousto.ec:setAction', 'purchase', {
        id: orderId,
        revenue: totalPrice,
        shipping: shippingPrice,
        coupon: promoCode,
      })
      ga('gousto.send', 'pageview')
    }

    dispatch(
      trackAffiliatePurchase({
        orderId,
        total: total || '',
        commissionGroup: 'FIRSTPURCHASE',
        promoCode,
        isSignup: true,
      }),
    )
    dispatch(clearTapjoyData())
    dispatch(clearRoktData())
  }

export function validatePassword(password) {
  return async (dispatch) => {
    if (password) {
      try {
        await validateUserPassword(password, 2)

        dispatch({ type: actionTypes.CHECKOUT_PASSWORD_VALIDATION_RULES_SET, password, errors: [] })
      } catch (err) {
        dispatch({
          type: actionTypes.CHECKOUT_PASSWORD_VALIDATION_RULES_SET,
          errors: err.errors,
          password,
        })
        logger.error({ message: err.message, errors: err })
      }
    } else {
      dispatch({
        type: actionTypes.CHECKOUT_PASSWORD_VALIDATION_RULES_SET,
        errors: passwordRules,
        password,
      })
    }
  }
}

export function fetchGoustoRef() {
  return async (dispatch, getState) => {
    let goustoRef = getState().checkout.get('goustoRef')
    if (!goustoRef) {
      const { data } = await fetchReference()
      goustoRef = data.goustoRef

      dispatch({
        type: actionTypes.CHECKOUT_SET_GOUSTO_REF,
        goustoRef,
      })
      dispatch(feLoggingLogEvent(logLevels.info, 'fetchGoustoRef: fetched'))
    } else {
      dispatch(feLoggingLogEvent(logLevels.info, 'fetchGoustoRef: already present'))
    }
  }
}

export function clearGoustoRef() {
  return async (dispatch) => {
    dispatch({
      type: actionTypes.CHECKOUT_SET_GOUSTO_REF,
      goustoRef: null,
    })
    dispatch(feLoggingLogEvent(logLevels.info, 'clearGoustoRef'))
  }
}

export function trackSignupPageChange(step) {
  return (dispatch) => {
    dispatch({ type: actionTypes.SIGNUP_TRACKING_STEP_CHANGE, step })
  }
}

export const trackCheckoutButtonPressed = (type, property) => {
  const tracking = {
    actionType: type,
    seCategory: 'Checkout',
    ...property,
  }

  return {
    type: actionTypes.TRACKING,
    trackingData: tracking,
    gtmEvent: tracking,
  }
}

export function trackingOrderPlaceAttempt({ pricing }) {
  return (dispatch, getState) => {
    const state = getState()

    dispatch({
      type: actionTypes.CHECKOUT_ORDER_PLACE_ATTEMPT,
      trackingData: {
        actionType: trackingKeys.placeOrderAttempt,
        order_id: getPreviewOrderId(state),
        order_total: pricing?.grossTotal,
        promo_code: pricing?.promoCode,
        payment_provider: 'checkout',
      },
    })
  }
}

export function trackingOrderPlaceAttemptFailed() {
  return (dispatch, getState) => {
    const errorMessages = getFormSyncErrors('payment')(getState()).payment

    dispatch({
      type: actionTypes.CHECKOUT_ORDER_PLACE_ATTEMPT_FAILED,
      trackingData: {
        actionType: trackingKeys.placeOrderFailAttempt,
        missing_fields: Object.values(errorMessages),
      },
    })
  }
}

export function trackingOrderPlaceAttemptSucceeded({ pricing }) {
  return (dispatch, getState) => {
    const state = getState()
    const { form } = state
    const deliveryInputs = Immutable.fromJS(form[deliveryFormName].values)
    const intervalId = deliveryInputs.getIn(['delivery', 'interval_id'], '1')

    dispatch({
      type: actionTypes.CHECKOUT_ORDER_PLACE_ATTEMPT_SUCCEEDED,
      trackingData: {
        actionType: trackingKeys.placeOrderAttemptComplete,
        order_id: getPreviewOrderId(state),
        order_total: pricing?.grossTotal,
        promo_code: pricing?.promoCode,
        interval_id: intervalId,
        payment_provider: 'checkout',
      },
    })
  }
}

export function trackingCardTokenizationFailed(err) {
  return (dispatch) => {
    dispatch({
      type: actionTypes.CHECKOUT_CARD_TOKENIZATION_FAILED,
      trackingData: {
        actionType: 'CardTokenization Failed',
        error_reason: err,
      },
    })
  }
}

export function trackingCardTokenizationSuccessfully() {
  return (dispatch) => {
    dispatch({
      type: actionTypes.CHECKOUT_CARD_TOKENIZATION_SUCCEEDED,
      trackingData: {
        actionType: 'CardTokenization Succeededs',
      },
    })
  }
}

export function trackPromocodeChange(promocode, added) {
  return (dispatch) =>
    dispatch({
      type: 'TRACKING_PROMOCODE_CHANGE',
      trackingData: {
        actionType: added ? 'Promocode Applied' : 'Promocode Removed',
        promocode,
      },
    })
}

export function fetchPayPalClientToken() {
  return async (dispatch) => {
    try {
      const { data } = await fetchPayPalToken()

      dispatch({
        type: actionTypes.PAYMENT_SET_PAYPAL_CLIENT_TOKEN,
        token: data.clientToken,
      })
    } catch (err) {
      logger.error({
        message: `${actionTypes.PAYPAL_TOKEN_FETCH_FAILED} - ${err.message}`,
        errors: [err],
      })
      dispatch(
        trackCheckoutError(actionTypes.PAYPAL_TOKEN_FETCH_FAILED, true, 'fetchPayPalClientToken'),
      )
      dispatch(error(actionTypes.PAYPAL_TOKEN_FETCH_FAILED, true))
    }
  }
}

export function clearPayPalErrors() {
  return (dispatch) => {
    dispatch({
      type: actionTypes.CHECKOUT_PAYPAL_ERRORS_CLEAR,
    })
  }
}

export function clearPayPalClientToken() {
  return (dispatch) => {
    dispatch({
      type: actionTypes.PAYMENT_SET_PAYPAL_CLIENT_TOKEN,
      token: null,
    })
  }
}

export function setCurrentPaymentMethod(paymentMethod, options = {}) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.PAYMENT_SET_PAYMENT_METHOD,
      paymentMethod,
    })

    const state = getState()

    if (
      paymentMethod === PaymentMethod.PayPal &&
      state.checkout.get('paypalErrors').size &&
      !state.checkout.get('paypalErrorsReported')
    ) {
      dispatch({ type: actionTypes.CHECKOUT_PAYPAL_ERRORS_REPORTED })
      dispatch(
        feLoggingLogEvent(logLevels.info, 'PayPal signup attempt failed because of init issue'),
      )
    }

    if (!options.disableTracking) {
      const trackingKey =
        paymentMethod === PaymentMethod.Card
          ? trackingKeys.selectCardPayment
          : trackingKeys.selectPayPalPayment
      dispatch(trackUTMAndPromoCode(trackingKey))
    }
  }
}

export function setPayPalDeviceData(deviceData) {
  return (dispatch) => {
    dispatch({
      type: actionTypes.PAYMENT_SET_PAYPAL_DEVICE_DATA,
      deviceData,
    })
  }
}

export const setPayPalNonce = (nonce) => ({
  type: actionTypes.PAYMENT_SET_PAYPAL_NONCE,
  nonce,
})

export function firePayPalError(err) {
  return (dispatch) => {
    logger.error({ message: `${actionTypes.PAYPAL_ERROR} - ${err.message}`, errors: [err] })
    dispatch(trackCheckoutError(actionTypes.PAYPAL_ERROR, true, 'firePayPalError'))
    dispatch(error(actionTypes.PAYPAL_ERROR, true))
  }
}

export const checkoutStepIndexReached = (stepIndex) => (dispatch) => {
  dispatch({
    type: actionTypes.CHECKOUT_STEP_INDEX_REACHED,
    stepIndex,
  })
}
